import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePagination } from "../../shared/hooks/usePagination";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { IJobPackage } from "../../shared/interfaces/job-package/IJobPackage";
import { JobPackageService } from "./service";
import { Card, CardBody, Col } from "react-bootstrap";
import Select from "react-select";
import { CustomPagination } from "../../shared/components/CustomPagination";
import DeleteModal from "../../Common/DeleteModal";

const PER_PAGE_DEFAULT = 10;

const JobPackageList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [list, setList] = useState<IJobPackage[] | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);
    const [packageNameToDelete, setPackageNameToDelete] = useState<string>("");

    const {
        page,
        handlePages,
        pagination,
        totalPages,
        setTotalPages,
        changeFilterHandler,
        goToPreviousPage,
        goToNextPage,
    } = usePagination({
        perPage: PER_PAGE_DEFAULT,
    });

    const entriesPerPageOptions = [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
    ];

    const fetchData = async () => {
        try {
            const response = await JobPackageService.get(pagination);
            setList(response.data);
            setTotalPages(response.totalPages);
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [pagination]);

    const handleDeleteClick = (item: IJobPackage) => {
        setItemToDelete(item.id);
        setPackageNameToDelete(item.name);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (itemToDelete !== null) {
            try {
                await JobPackageService.delete(itemToDelete);
                SuccessToast(t("jobPackage.successDelete"));
                setShowDeleteModal(false);
                setItemToDelete(null);
                fetchData();
            } catch (error) {
                ErrorToast(error);
            }
        }
    };

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody>
                        <div className="text-end mb-3">
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate("/job-packages/create")}
                            >
                                <i className="ti ti-plus f-18"></i> {t("jobPackage.add")}
                            </button>
                        </div>

                        <div className="d-sm-flex align-items-center mb-3">
                            <ul className="list-inline me-auto my-1">
                                <li className="list-inline-item">
                                    <Select
                                        placeholder={t("general.entriesPerPage")}
                                        options={entriesPerPageOptions}
                                        onChange={(selected) =>
                                            changeFilterHandler("perPage", selected?.value ?? PER_PAGE_DEFAULT)
                                        }
                                        defaultValue={entriesPerPageOptions[0]}
                                        classNamePrefix="react-select"
                                    />
                                </li>
                            </ul>
                            <ul className="list-inline ms-auto my-1">
                                <li className="list-inline-item">
                                    <input
                                        className="form-control"
                                        type="text"
                                        placeholder={t("general.search")}
                                        onChange={(e) => changeFilterHandler("term", e.target.value)}
                                    />
                                </li>
                            </ul>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>{t("jobPackage.name")}</th>
                                        <th>{t("general.updatedAt")}</th>
                                        <th className="text-end">{t("general.actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list?.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                                            <td className="text-end">
                                                <ul className="list-inline mb-0">
                                                    <li className="list-inline-item" onClick={() => navigate(`/job-packages/edit/${item.id}`)}>
                                                        <span className="avtar avtar-s btn-link-success btn-pc-default" role="button">
                                                            <i className="ti ti-edit f-20"></i>
                                                        </span>
                                                    </li>
                                                    <li className="list-inline-item" onClick={() => handleDeleteClick(item)}>
                                                        <span className="avtar avtar-s btn-link-danger btn-pc-default" role="button">
                                                            <i className="ti ti-trash f-20"></i>
                                                        </span>
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <CustomPagination
                            page={page}
                            totalPages={totalPages}
                            handlePages={handlePages}
                            goToPreviousPage={goToPreviousPage}
                            goToNextPage={goToNextPage}
                        />
                    </CardBody>
                </Card>
            </Col>

            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={packageNameToDelete}
            />
        </React.Fragment>
    );
};

export default JobPackageList;
